import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import documentService from '../../../services/document';
import presentationService from '../../../services/presentation';
import { saveWizardPresentation, updateWizardPresentation } from '../../../slices/presentation';
import { generateTimeIntervals } from '../../../utils';
import { normalizeItems } from '../../../utils/normalizePresentation';

function buildSlidesPayload(slides) {
  return slides.map((slide) => ({
    data_source: slide.data_source || '',
    intent: slide.intent || '',
    query: slide.query || '',
    elements: (slide.elements || []).map((el) => ({
      type: el.type,
      x: el.x,
      y: el.y,
      width: el.width,
      height: el.height,
      text: el.text || '',
      data_source: el.data_source || '',
      query: el.query || '',
      query_prompt: el.query_prompt || '',
      llm_instruction: el.llm_instruction || '',
      intent: el.intent || '',
      time_parameters: el.time_parameters || [],
      visualization: el.visualization || '',
      variable: el.variable || 'No',
      columns: el.columns || undefined,
      series: el.series || undefined,
      categories: el.categories || undefined,
      chart_type: el.chart_type || undefined,
    })),
  }));
}

export function usePresentationWizard({ editId }) {
  const isEditMode = !!editId;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Details step
  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');

  // Wizard navigation
  const [activeStep, setActiveStep] = React.useState('details');
  const [editLoading, setEditLoading] = React.useState(false);

  // Upload step
  const fileInputRef = React.useRef(null);
  const [dragActive, setDragActive] = React.useState(false);
  const [pptxFile, setPptxFile] = React.useState(null);
  const [pptxError, setPptxError] = React.useState(null);
  const [extractLoading, setExtractLoading] = React.useState(false);
  const [extractedMetadata, setExtractedMetadata] = React.useState(null);
  const [templateId, setTemplateId] = React.useState(null);

  // Layout step
  const [slides, setSlides] = React.useState([]);
  const [selectedSlideIndex, setSelectedSlideIndex] = React.useState(0);

  // Schedule step
  const [scheduleEnabled, setScheduleEnabled] = React.useState(false);
  const [recurrence, setRecurrence] = React.useState('');
  const [scheduleDay, setScheduleDay] = React.useState('');
  const [scheduleMonth, setScheduleMonth] = React.useState('');
  const [scheduleTime, setScheduleTime] = React.useState('');
  const [scheduleEmail, setScheduleEmail] = React.useState('');
  const [customInterval, setCustomInterval] = React.useState(1);
  const [customUnit, setCustomUnit] = React.useState('months');

  // Test PPT
  const [testPptLoading, setTestPptLoading] = React.useState(false);
  const [testPptResult, setTestPptResult] = React.useState(null);

  // Number format
  const [decimalPlaces, setDecimalPlaces] = React.useState(2);

  // Incremental save state
  const [savedPresentationId, setSavedPresentationId] = React.useState(editId ? Number(editId) : null);
  const [layoutSaveLoading, setLayoutSaveLoading] = React.useState(false);
  const [scheduleSaveLoading, setScheduleSaveLoading] = React.useState(false);

  // Submission
  const [submitting, setSubmitting] = React.useState(false);
  const [validationErrors, setValidationErrors] = React.useState({});
  const timeIntervals = React.useMemo(() => generateTimeIntervals(), []);

  const stepOrder = isEditMode
    ? ['details', 'layout', 'schedule']
    : ['details', 'creation-method', 'layout', 'schedule'];

  const steps = isEditMode
    ? [
        { id: 'details', label: 'Presentation Details' },
        { id: 'layout', label: 'Configure Layout' },
        { id: 'schedule', label: 'Schedule' },
      ]
    : [
        { id: 'details', label: 'Presentation Details' },
        { id: 'creation-method', label: 'Upload Presentation' },
        { id: 'layout', label: 'Configure Layout' },
        { id: 'schedule', label: 'Schedule' },
      ];

  const currentStepIndex = stepOrder.indexOf(activeStep);
  const isFirstStep = currentStepIndex <= 0;
  const isLastStep = currentStepIndex >= stepOrder.length - 1;

  const DYNAMIC_ELEMENT_TYPES = ['table', 'chart', 'text', 'textbox'];

  // True when every slide with dynamic elements has data_source + (intent or query) set,
  // either at the slide level or on every individual dynamic element.
  const isLayoutComplete = React.useMemo(() => {
    if (!slides.length) return false;
    return slides.every((slide) => {
      const dynamicEls = (slide.elements ?? []).filter((el) =>
        DYNAMIC_ELEMENT_TYPES.includes(el.type)
      );
      if (dynamicEls.length === 0) return true;
      const slideConfigured = Boolean(slide.data_source && (slide.intent || slide.query));
      return dynamicEls.every(
        (el) => (el.data_source && (el.intent || el.query)) || slideConfigured
      );
    });
  }, [slides]); // eslint-disable-line react-hooks/exhaustive-deps

  // Validation
  const validateStep = React.useCallback((step) => {
    const errors = {};
    if (step === 'details') {
      if (!name.trim()) errors.name = 'Presentation name is required';
    }
    if (step === 'creation-method') {
      if (!pptxFile) errors.pptxFile = 'Please upload a .pptx file';
    }
    if (step === 'layout') {
      if (!slides.length) errors.slides = 'At least one slide is required';
      else if (!slides.every((slide) => {
        const dynamicEls = (slide.elements ?? []).filter((el) =>
          DYNAMIC_ELEMENT_TYPES.includes(el.type)
        );
        if (dynamicEls.length === 0) return true;
        const slideConfigured = Boolean(slide.data_source && (slide.intent || slide.query));
        return dynamicEls.every(
          (el) => (el.data_source && (el.intent || el.query)) || slideConfigured
        );
      })) {
        errors.slides = 'All slides must have a data source and query/intent configured';
      }
    }
    if (step === 'schedule' && scheduleEnabled) {
      if (!recurrence) errors.recurrence = 'Select a recurrence type';
      if (!scheduleTime) errors.scheduleTime = 'Select a time';
      if (!scheduleEmail.trim()) {
        errors.scheduleEmail = 'Email destination is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(scheduleEmail.trim())) {
        errors.scheduleEmail = 'Enter a valid email address';
      }
      if (['monthly', 'quarterly'].includes(recurrence) && !scheduleDay) {
        errors.scheduleDay = 'Select a day';
      }
      if (recurrence === 'yearly') {
        if (!scheduleMonth) errors.scheduleMonth = 'Select a month';
        if (!scheduleDay) errors.scheduleDay = 'Select a day';
      }
      if (recurrence === 'custom') {
        if (!scheduleDay) errors.scheduleDay = 'Select a day';
        if (!customInterval || customInterval < 1) errors.customInterval = 'Enter a valid interval';
      }
    }
    return errors;
  }, [name, pptxFile, slides, scheduleEnabled, recurrence, scheduleTime, scheduleEmail, scheduleDay, scheduleMonth, customInterval]);

  const validateAllSteps = React.useCallback(() => {
    const allErrors = {};
    for (const step of stepOrder) {
      Object.assign(allErrors, validateStep(step));
    }
    return allErrors;
  }, [validateStep]);

  // Sync extracted metadata slides
  React.useEffect(() => {
    const raw = extractedMetadata?.metadata?.slides ?? [];
    setSlides(Array.isArray(raw) ? JSON.parse(JSON.stringify(raw)) : []);
  }, [extractedMetadata]);

  // Load existing presentation in edit mode
  React.useEffect(() => {
    if (!editId) return;
    let cancelled = false;
    setEditLoading(true);
    presentationService.getOnePresentation(editId).then((res) => {
      if (cancelled) return;
      const data = res.data;
      setName(data.title || '');
      const normalized = normalizeItems(data.items);
      setDescription(normalized.description || '');
      setSlides(Array.isArray(normalized.slides) ? normalized.slides : []);
      presentationService.loadSchedule({ id: Number(editId), mode: 'presentation' }).then((schedRes) => {
        if (cancelled || !schedRes.data) return;
        const sched = schedRes.data;
        if (sched && sched.recurrence) {
          setScheduleEnabled(true);
          setRecurrence(sched.recurrence || '');
          setScheduleDay(sched.day || '');
          setScheduleTime(sched.time || '');
          setScheduleEmail(Array.isArray(sched.destinations) ? sched.destinations[0] || '' : '');
        }
      }).catch(() => {});
    }).catch(() => {
      if (!cancelled) setPptxError('Failed to load presentation data');
    }).finally(() => {
      if (!cancelled) setEditLoading(false);
    });
    return () => { cancelled = true; };
  }, [editId]);

  // Handlers
  const handleCancel = () => navigate('/settings');

  const handleSaveDraft = () => {
    console.log('Saving presentation as draft...');
  };

  const handleSubmit = async () => {
    const errors = validateAllSteps();
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) {
      for (const step of stepOrder) {
        const stepErrors = validateStep(step);
        if (Object.keys(stepErrors).length > 0) {
          setActiveStep(step);
          break;
        }
      }
      return;
    }

    setPptxError(null);
    setSubmitting(true);
    try {
      if (pptxFile) {
        if (!pptxFile.name?.toLowerCase().endsWith('.pptx')) {
          setPptxError('Please select a PowerPoint file (.pptx)');
          setSubmitting(false);
          return;
        }
        try {
          await documentService.extractMetadata(pptxFile);
        } catch (err) {
          const message = err.response?.data?.detail || err.message || 'Template upload failed';
          setPptxError(typeof message === 'string' ? message : JSON.stringify(message));
          setSubmitting(false);
          return;
        }
      }

      const schedule = scheduleEnabled
        ? {
            recurrence: recurrence === 'custom'
              ? `every_${customInterval}_${customUnit}`
              : recurrence,
            day: String(scheduleDay),
            month: recurrence === 'yearly' ? String(scheduleMonth) : undefined,
            time: scheduleTime,
            destinations: [scheduleEmail],
            ...(recurrence === 'custom' && { interval: customInterval, unit: customUnit }),
          }
        : null;

      const slidesPayload = slides.map((slide) => ({
        elements: (slide.elements || []).map((el) => ({
          type: el.type,
          x: el.x,
          y: el.y,
          width: el.width,
          height: el.height,
          text: el.text || '',
          data_source: el.data_source || '',
          query: el.query || '',
          query_prompt: el.query_prompt || '',
          llm_instruction: el.llm_instruction || '',
          intent: el.intent || '',
          time_parameters: el.time_parameters || [],
          visualization: el.visualization || '',
          variable: el.variable || 'No',
          columns: el.columns || undefined,
          series: el.series || undefined,
          categories: el.categories || undefined,
          chart_type: el.chart_type || undefined,
        })),
      }));

      const payload = {
        title: name,
        description,
        slides: slidesPayload,
        schedule,
      };

      let savedPresId;
      if (isEditMode) {
        await dispatch(updateWizardPresentation({ id: Number(editId), ...payload })).unwrap();
        savedPresId = Number(editId);
      } else {
        const result = await dispatch(saveWizardPresentation(payload)).unwrap();
        savedPresId = result.id;
      }

      navigate('/settings');
    } catch (err) {
      const message = err?.message || err?.detail || 'Failed to save presentation';
      setPptxError(typeof message === 'string' ? message : JSON.stringify(message));
    } finally {
      setSubmitting(false);
    }
  };

  const handleTestPpt = async (slideIndex) => {
    if (!templateId) {
      setPptxError('No template found — please upload a .pptx and proceed to the layout step first');
      return;
    }
    const targetSlide = slides[slideIndex];
    if (!targetSlide) {
      setPptxError('No slide selected to test');
      return;
    }
    const slidePayload = {
      data_source: targetSlide.data_source || '',
      intent: targetSlide.intent || '',
      query: targetSlide.query || '',
      query_prompt: targetSlide.query_prompt || '',
      llm_instruction: targetSlide.llm_instruction || '',
      time_parameters: targetSlide.time_parameters || [],
      elements: (targetSlide.elements || []).map((el) => ({
        type: el.type,
        x: el.x,
        y: el.y,
        width: el.width,
        height: el.height,
        text: el.text || '',
        data_source: el.data_source || '',
        query: el.query || '',
        query_prompt: el.query_prompt || '',
        llm_instruction: el.llm_instruction || '',
        intent: el.intent || '',
        time_parameters: el.time_parameters || [],
        visualization: el.visualization || '',
        variable: el.variable || 'No',
        columns: el.columns || undefined,
        series: el.series || undefined,
        categories: el.categories || undefined,
        chart_type: el.chart_type || undefined,
      })),
    };
    setTestPptLoading(true);
    setPptxError(null);
    try {
      const res = await presentationService.testSlide(templateId, slidePayload, slideIndex, decimalPlaces);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${name || 'test'}_slide${slideIndex + 1}_preview.pptx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      let message = err?.message || 'Failed to generate test PPT';
      const responseData = err?.response?.data;
      if (responseData instanceof Blob) {
        try {
          const text = await responseData.text();
          const parsed = JSON.parse(text);
          if (parsed?.detail) message = typeof parsed.detail === 'string' ? parsed.detail : JSON.stringify(parsed.detail);
        } catch {
          // ignore parse errors
        }
      } else if (responseData?.detail) {
        message = typeof responseData.detail === 'string' ? responseData.detail : JSON.stringify(responseData.detail);
      }
      setPptxError(message);
    } finally {
      setTestPptLoading(false);
    }
  };

  const handleBrowseFiles = () => fileInputRef.current?.click();

  const handleFileSelect = (file) => {
    const lower = file.name?.toLowerCase() || '';
    const docExts = ['.doc', '.docx', '.pdf', '.eml', '.msg'];
    if (lower.endsWith('.pptx')) {
      setPptxFile(file);
      setPptxError(null);
    } else if (docExts.some((ext) => lower.endsWith(ext))) {
      console.log('Document file selected:', file.name);
    }
  };

  const handleFileChange = (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    handleFileSelect(files[0]);
    e.target.value = '';
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;
    handleFileSelect(files[0]);
  };

  const clearPptx = () => {
    setPptxFile(null);
    setPptxError(null);
  };

  const handlePrev = () => {
    if (!isFirstStep) {
      setActiveStep(stepOrder[currentStepIndex - 1]);
    }
  };

  const handleNext = async () => {
    const errors = validateStep(activeStep);
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) return;

    // Last step (schedule) is handled below with its own navigation
    if (isLastStep && activeStep !== 'schedule') {
      navigate('/settings');
      return;
    }

    // ── Upload step ──────────────────────────────────────────────────────────
    if (activeStep === 'creation-method') {
      if (!pptxFile) {
        setPptxError('Please upload a .pptx file first');
        return;
      }
      if (!pptxFile.name?.toLowerCase().endsWith('.pptx')) {
        setPptxError('Please select a PowerPoint file (.pptx)');
        return;
      }
      setPptxError(null);
      setExtractLoading(true);
      try {
        const res = await documentService.extractMetadata(pptxFile);
        setExtractedMetadata(res.data);
        if (res.data?.template_id) {
          setTemplateId(res.data.template_id);
        }
        setActiveStep(stepOrder[currentStepIndex + 1]);
      } catch (err) {
        const message = err.response?.data?.detail || err.message || 'Failed to extract metadata';
        setPptxError(typeof message === 'string' ? message : JSON.stringify(message));
      } finally {
        setExtractLoading(false);
      }
      return;
    }

    // ── Layout step → save presentation to DB ────────────────────────────────
    if (activeStep === 'layout') {
      setPptxError(null);
      setLayoutSaveLoading(true);
      try {
        const slidesPayload = buildSlidesPayload(slides);
        let presId;

        if (isEditMode) {
          await dispatch(updateWizardPresentation({
            id: Number(editId),
            title: name,
            description,
            slides: slidesPayload,
            is_completed: false,
            decimal_places: decimalPlaces,
            template_id: templateId,
          })).unwrap();
          presId = Number(editId);
        } else {
          const result = await dispatch(saveWizardPresentation({
            title: name,
            description,
            slides: slidesPayload,
            is_completed: false,
            decimal_places: decimalPlaces,
            template_id: templateId,
          })).unwrap();
          presId = result.id;
        }

        setSavedPresentationId(presId);
        setActiveStep(stepOrder[currentStepIndex + 1]);
      } catch (err) {
        const message = err?.message || err?.detail || 'Failed to save presentation';
        setPptxError(typeof message === 'string' ? message : JSON.stringify(message));
      } finally {
        setLayoutSaveLoading(false);
      }
      return;
    }

    // ── Schedule step → save schedule ────────────────────────────────────────
    if (activeStep === 'schedule') {
      const presId = savedPresentationId;
      if (presId && scheduleEnabled) {
        const schedErrors = validateStep('schedule');
        if (Object.keys(schedErrors).length > 0) {
          setValidationErrors(schedErrors);
          return;
        }
      }
      setScheduleSaveLoading(true);
      setPptxError(null);
      try {
        if (presId && scheduleEnabled) {
          // Remove existing schedule first (edit mode may have one)
          try { await presentationService.deleteSchedule(presId); } catch (_) { /* none to delete */ }

          await presentationService.schedulePresentation({
            presentation: presId,
            title: name,
            recurrence: recurrence === 'custom'
              ? `every_${customInterval}_${customUnit}`
              : recurrence,
            day: String(scheduleDay),
            time: scheduleTime,
            destinations: [scheduleEmail],
          });
        }

        // Mark presentation as completed
        if (presId) {
          const slidesPayload = buildSlidesPayload(slides);
          await dispatch(updateWizardPresentation({
            id: presId,
            title: name,
            description,
            slides: slidesPayload,
            is_completed: true,
            decimal_places: decimalPlaces,
          })).unwrap();
        }

        navigate('/settings');
      } catch (err) {
        const message = err?.response?.data?.detail || err?.message || 'Failed to save schedule';
        setPptxError(typeof message === 'string' ? message : JSON.stringify(message));
      } finally {
        setScheduleSaveLoading(false);
      }
      return;
    }

    setActiveStep(stepOrder[currentStepIndex + 1]);
  };

  return {
    // State
    isEditMode,
    activeStep,
    setActiveStep,
    editLoading,
    name,
    setName,
    description,
    setDescription,
    pptxFile,
    pptxError,
    setPptxError,
    templateId,
    testPptResult,
    dragActive,
    extractLoading,
    slides,
    setSlides,
    selectedSlideIndex,
    setSelectedSlideIndex,
    scheduleEnabled,
    setScheduleEnabled,
    recurrence,
    setRecurrence,
    scheduleDay,
    setScheduleDay,
    scheduleMonth,
    setScheduleMonth,
    scheduleTime,
    setScheduleTime,
    scheduleEmail,
    setScheduleEmail,
    customInterval,
    setCustomInterval,
    customUnit,
    setCustomUnit,
    testPptLoading,
    decimalPlaces,
    setDecimalPlaces,
    savedPresentationId,
    layoutSaveLoading,
    scheduleSaveLoading,
    submitting,
    validationErrors,
    setValidationErrors,
    timeIntervals,

    // Wizard
    steps,
    stepOrder,
    currentStepIndex,
    isFirstStep,
    isLastStep,
    isLayoutComplete,

    // Handlers
    handleCancel,
    handleSaveDraft,
    handleSubmit,
    handleTestPpt,
    handlePrev,
    handleNext,
    handleBrowseFiles,
    handleFileChange,
    handleDrag,
    handleDrop,
    clearPptx,
    validateStep,

    // Refs
    fileInputRef,
  };
}
